import { useState } from 'react';
import {
  runBulk,
  templateMemicuRestart,
  type BulkRequest,
  type BulkResponse,
  type CameraTagWithCount,
  type CameraTemplate,
} from '../lib/api';
import { useBahasa } from '../i18n';
import { RETENSI_MAX_HARI } from '../lib/retensi';

/**
 * Bilah aksi massal (§G11.2), menempel di kaki halaman saat ada yang terpilih.
 *
 * Jalur yang dimaksudkan bukan "centang 40 kartu satu per satu" — itu
 * pengulangan yang dipindah tempat, bukan operasi massal. Yang dimaksudkan:
 * **saring dengan tag (§G11.1) → pilih semua → satu tindakan**, dan karena itu
 * "pilih semua yang cocok saringan sekarang" duduk di tempat yang paling
 * gampang dijangkau.
 */

type Aksi = BulkRequest['action'];

export function CameraBulkBar({
  terpilih,
  totalTerlihat,
  tags,
  templates,
  admin,
  onPilihSemua,
  onBersihkan,
  onSelesai,
}: {
  terpilih: string[];
  totalTerlihat: number;
  tags: CameraTagWithCount[];
  templates: CameraTemplate[];
  admin: boolean;
  onPilihSemua: () => void;
  onBersihkan: () => void;
  /** Dipanggil sesudah operasi yang mengubah data, supaya daftarnya dimuat ulang. */
  onSelesai: () => Promise<void> | void;
}) {
  const { t } = useBahasa();
  const [aksi, setAksi] = useState<Aksi | ''>('');
  const [maxDay, setMaxDay] = useState('');
  const [ikutGlobal, setIkutGlobal] = useState(false);
  const [record, setRecord] = useState(true);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [pemeliharaanOn, setPemeliharaanOn] = useState(true);
  const [catatan, setCatatan] = useState('');
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState<string | null>(null);
  const [hasil, setHasil] = useState<BulkResponse | null>(null);
  const [gagalTerbuka, setGagalTerbuka] = useState(true);
  const [berhasilTerbuka, setBerhasilTerbuka] = useState(false);

  if (terpilih.length === 0 && !hasil) return null;

  const tpl = templates.find((x) => x.id === templateId);

  /**
   * Peringatan ekor segmen muncul **hanya** untuk tindakan yang benar-benar
   * menyentuh proses recorder. Peringatan yang selalu muncul berhenti dibaca,
   * dan yang paling perlu dibaca justru yang ini.
   */
  const perluPeringatanRestart =
    aksi === 'restart' ||
    aksi === 'set_record' ||
    // Menandai pemeliharaan MENGHENTIKAN recorder-nya (§D10.2) — ekor segmen
    // yang sedang ditulis ikut terpotong, sama seperti restart.
    (aksi === 'set_maintenance' && pemeliharaanOn) ||
    (aksi === 'apply_template' && tpl !== undefined && templateMemicuRestart(tpl.values));

  function rakit(): BulkRequest | null {
    const cameraIds = terpilih;
    switch (aksi) {
      case 'set_retention':
        return {
          action: 'set_retention',
          cameraIds,
          maxDay: ikutGlobal ? null : Number(maxDay),
        };
      case 'set_record':
        return { action: 'set_record', cameraIds, recordEnabled: record };
      case 'add_tags':
      case 'remove_tags':
        return { action: aksi, cameraIds, tagIds };
      case 'apply_template':
        return { action: 'apply_template', cameraIds, templateId };
      case 'set_maintenance':
        return { action: 'set_maintenance', cameraIds, on: pemeliharaanOn, note: catatan.trim() || null };
      case 'test':
      case 'restart':
        return { action: aksi, cameraIds };
      default:
        return null;
    }
  }

  const siap =
    aksi !== '' &&
    // §D6 — batas atasnya sama dengan form satuan. Operasi massal yang
    // menerima angka yang ditolak form satuan berarti pagarnya tinggal
    // sebuah saran.
    (aksi !== 'set_retention' ||
      ikutGlobal ||
      (Number(maxDay) >= 1 && Number(maxDay) <= RETENSI_MAX_HARI)) &&
    (aksi !== 'add_tags' || tagIds.length > 0) &&
    (aksi !== 'remove_tags' || tagIds.length > 0) &&
    (aksi !== 'apply_template' || templateId !== '');

  async function jalankan() {
    const body = rakit();
    if (!body) return;
    setSibuk(true);
    setGalat(null);
    try {
      const r = await runBulk(body);
      setHasil(r);
      setGagalTerbuka(r.failCount > 0);
      setBerhasilTerbuka(false);
      // Uji koneksi tidak mengubah apa pun; memuat ulang 96 kartu untuknya cuma
      // membuang satu putaran permintaan.
      if (body.action !== 'test') await onSelesai();
    } catch (e) {
      setGalat((e as Error).message);
    } finally {
      setSibuk(false);
    }
  }

  async function ulangiYangGagal() {
    if (!hasil) return;
    const ids = hasil.results.filter((r) => !r.ok).map((r) => r.cameraId);
    if (ids.length === 0) return;
    const body = rakit();
    if (!body) return;
    setSibuk(true);
    try {
      const r = await runBulk({ ...body, cameraIds: ids } as BulkRequest);
      setHasil(r);
      setGagalTerbuka(r.failCount > 0);
      if (body.action !== 'test') await onSelesai();
    } catch (e) {
      setGalat((e as Error).message);
    } finally {
      setSibuk(false);
    }
  }

  const gagal = hasil?.results.filter((r) => !r.ok) ?? [];
  const berhasil = hasil?.results.filter((r) => r.ok) ?? [];

  return (
    <div className="sticky bottom-0 z-30 mt-4 rounded-lg border border-line bg-surface p-3 shadow-lg">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm">
          {t('{n} selected', { n: terpilih.length })}
        </span>

        {/* Ini jalur yang sebenarnya dipakai: saring dulu, lalu satu klik. */}
        {terpilih.length < totalTerlihat && (
          <button
            type="button"
            onClick={onPilihSemua}
            className="text-sm text-accent-text underline underline-offset-2"
          >
            {t('Select all {n} matching the current filter', { n: totalTerlihat })}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            onBersihkan();
            setHasil(null);
            setAksi('');
          }}
          className="text-sm underline underline-offset-2"
        >
          {t('Clear selection')}
        </button>

        <select
          value={aksi}
          onChange={(e) => {
            setAksi(e.target.value as Aksi);
            setHasil(null);
          }}
          className="ml-auto min-h-tap rounded-md border border-line bg-surface px-2 text-sm"
        >
          <option value="">{t('Choose an action…')}</option>
          {admin && <option value="set_retention">{t('Change retention')}</option>}
          {admin && <option value="set_record">{t('Enable / disable recording')}</option>}
          <option value="add_tags">{t('Add tags')}</option>
          <option value="remove_tags">{t('Remove tags')}</option>
          {admin && <option value="apply_template">{t('Apply template')}</option>}
          <option value="set_maintenance">{t('Mark / end maintenance')}</option>
        <option value="test">{t('Test connection')}</option>
          <option value="restart">{t('Restart recorder')}</option>
        </select>
      </div>

      {/* Parameter tindakan — hanya yang dipilih yang muncul. */}
      {aksi === 'set_retention' && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <label className="flex min-h-tap items-center gap-2">
            <input
              type="checkbox"
              checked={ikutGlobal}
              onChange={(e) => setIkutGlobal(e.target.checked)}
            />
            {t('Follow the global retention setting')}
          </label>
          {!ikutGlobal && (
            <>
              <label className="flex items-center gap-2">
                {t('Days')}
                <input
                  type="number"
                  min={1}
                  max={RETENSI_MAX_HARI}
                  value={maxDay}
                  onChange={(e) => setMaxDay(e.target.value)}
                  className="min-h-tap w-24 rounded-md border border-line bg-surface px-2"
                />
              </label>
              <span
                className={
                  Number(maxDay) > RETENSI_MAX_HARI ? 'text-xs text-red-600' : 'text-xs text-muted'
                }
              >
                {t('At most {n} days — recordings older than that are removed by the storage server itself.', {
                  n: RETENSI_MAX_HARI,
                })}
              </span>
            </>
          )}
        </div>
      )}

      {aksi === 'set_record' && (
        <div className="mt-3 flex flex-wrap gap-3 text-sm">
          <label className="flex min-h-tap items-center gap-2">
            <input type="radio" checked={record} onChange={() => setRecord(true)} />
            {t('View and Record')}
          </label>
          <label className="flex min-h-tap items-center gap-2">
            <input type="radio" checked={!record} onChange={() => setRecord(false)} />
            {t('View Only')}
          </label>
        </div>
      )}

      {(aksi === 'add_tags' || aksi === 'remove_tags') && (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {tags.map((tag) => {
            const on = tagIds.includes(tag.id);
            return (
              <li key={tag.id}>
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setTagIds(on ? tagIds.filter((i) => i !== tag.id) : [...tagIds, tag.id])
                  }
                  className={`min-h-tap rounded-md border px-2 text-xs ${
                    on ? 'border-accent text-accent-text' : 'border-line text-muted'
                  }`}
                >
                  {tag.name}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {aksi === 'apply_template' && (
        <div className="mt-3 text-sm">
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="min-h-tap rounded-md border border-line bg-surface px-2 text-sm"
          >
            <option value="">{t('Choose a template…')}</option>
            {templates.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
          {tpl && (
            <p className="mt-1 text-xs text-muted">
              {t('Overwrites only these settings: {daftar}. Credentials are never applied to existing cameras.', {
                daftar: Object.keys(tpl.values).join(', ') || t('nothing'),
              })}
            </p>
          )}
        </div>
      )}

      {/* Ongkosnya ditulis di layar, bukan cuma di spec (§G11.2). */}
      {perluPeringatanRestart && (
        <p className="mt-3 rounded-md border border-amber-500 px-3 py-2 text-xs">
          {t('This restarts the recorder on {n} cameras. The last few seconds of the segment being written are lost on each one — run it just after :00, :15, :30 or :45.', {
            n: terpilih.length,
          })}
        </p>
      )}

      {aksi === 'set_maintenance' && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <label className="flex min-h-tap items-center gap-2">
            <input type="radio" checked={pemeliharaanOn} onChange={() => setPemeliharaanOn(true)} />
            {t('Mark as maintenance')}
          </label>
          <label className="flex min-h-tap items-center gap-2">
            <input type="radio" checked={!pemeliharaanOn} onChange={() => setPemeliharaanOn(false)} />
            {t('End maintenance')}
          </label>
          {pemeliharaanOn && (
            <input
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              maxLength={255}
              placeholder={t('What is being done to this camera?')}
              className="min-h-tap flex-1 rounded-md border border-line bg-surface px-3 text-sm"
            />
          )}
        </div>
      )}

      {aksi === 'test' && (
        <p className="mt-3 text-xs text-muted">
          {t('Runs 8 at a time — cameras limit how many RTSP sessions they will accept at once.')}
        </p>
      )}

      {galat && (
        <p role="alert" className="mt-3 rounded-md border border-red-600 px-3 py-2 text-sm">
          {galat}
        </p>
      )}

      {aksi !== '' && (
        <div className="mt-3">
          <button
            type="button"
            disabled={!siap || sibuk || terpilih.length === 0}
            onClick={() => void jalankan()}
            className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-50"
          >
            {sibuk
              ? t('Working…')
              : t('Apply to {n} cameras', { n: terpilih.length })}
          </button>
        </div>
      )}

      {/* Hasil per kamera, bukan satu kata (§G11.2). Yang gagal terbentang,
          yang berhasil diciutkan: kegagalan sebagian adalah keadaan normal di
          sini, dan yang perlu dibaca orang adalah yang tidak ikut berubah. */}
      {hasil && (
        <div className="mt-3 border-t border-line pt-3 text-sm">
          <p>
            {t('{ok} succeeded, {fail} failed.', {
              ok: hasil.okCount,
              fail: hasil.failCount,
            })}
          </p>

          {gagal.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setGagalTerbuka((v) => !v)}
                aria-expanded={gagalTerbuka}
                className="text-sm text-red-600 underline underline-offset-2"
              >
                {t('{n} failed', { n: gagal.length })}
              </button>
              {gagalTerbuka && (
                <ul className="mt-1 space-y-0.5">
                  {gagal.map((r) => (
                    <li key={r.cameraId} className="text-xs">
                      <span className="font-medium">{r.name}</span>
                      {r.message ? ` — ${r.message}` : ''}
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                disabled={sibuk}
                onClick={() => void ulangiYangGagal()}
                className="mt-2 min-h-tap rounded-md border border-line px-3 text-sm"
              >
                {t('Retry only the {n} that failed', { n: gagal.length })}
              </button>
            </div>
          )}

          {berhasil.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setBerhasilTerbuka((v) => !v)}
                aria-expanded={berhasilTerbuka}
                className="text-sm underline underline-offset-2"
              >
                {t('{n} succeeded', { n: berhasil.length })}
              </button>
              {berhasilTerbuka && (
                <ul className="mt-1 space-y-0.5">
                  {berhasil.map((r) => (
                    <li key={r.cameraId} className="text-xs text-muted">
                      {r.name}
                      {r.message ? ` — ${r.message}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
